import { useState, useCallback } from 'react';
import { formulacionesService } from '../services';

/**
 * Hook personalizado para manejar operaciones CRUD de formulaciones
 */
export const useFormulaciones = () => {
  const [formulaciones, setFormulaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Cargar todas las formulaciones
   */
  const loadFormulaciones = useCallback(async (params = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await formulacionesService.getFormulaciones(params);

      if (result.success) {
        setFormulaciones(result.data || []);
        return { success: true, data: result.data };
      } else {
        setError(result.message || 'Error al cargar formulaciones');
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMsg = 'Error de conexión al cargar formulaciones';
      setError(errorMsg);
      console.error('Error loading formulaciones:', err);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Obtener formulación por ID
   */
  const getFormulacionById = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await formulacionesService.getFormulacion(id);

      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.message || 'Error al cargar formulación');
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMsg = 'Error de conexión al cargar formulación';
      setError(errorMsg);
      console.error('Error loading formulacion:', err);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Crear nueva formulación
   */
  const createFormulacion = useCallback(async (formulacionData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await formulacionesService.createFormulacion(formulacionData);

      if (result.success) {
        // Actualizar lista de formulaciones
        if (formulaciones.length > 0) {
          setFormulaciones([result.data, ...formulaciones]);
        }
        return {
          success: true,
          data: result.data,
          message: 'Formulación creada exitosamente'
        };
      } else {
        setError(result.message || 'Error al crear formulación');
        return {
          success: false,
          message: result.message,
          validation_errors: result.validation_errors,
          validationErrors: result.validationErrors
        };
      }
    } catch (err) {
      const errorMsg = 'Error de conexión al crear formulación';
      setError(errorMsg);
      console.error('Error creating formulacion:', err);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [formulaciones]);

  /**
   * Actualizar formulación existente
   */
  const updateFormulacion = useCallback(async (id, formulacionData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await formulacionesService.updateFormulacion(id, formulacionData);

      if (result.success) {
        // Actualizar lista de formulaciones
        if (formulaciones.length > 0) {
          const updatedFormulaciones = formulaciones.map(f =>
            f.id === id ? result.data : f
          );
          setFormulaciones(updatedFormulaciones);
        }
        return {
          success: true,
          data: result.data,
          message: 'Formulación actualizada exitosamente'
        };
      } else {
        setError(result.message || 'Error al actualizar formulación');
        return {
          success: false,
          message: result.message,
          validation_errors: result.validation_errors,
          validationErrors: result.validationErrors
        };
      }
    } catch (err) {
      const errorMsg = 'Error de conexión al actualizar formulación';
      setError(errorMsg);
      console.error('Error updating formulacion:', err);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [formulaciones]);

  /**
   * Eliminar formulación
   */
  const deleteFormulacion = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await formulacionesService.deleteFormulacion(id);

      if (result.success) {
        // Actualizar lista de formulaciones
        const updatedFormulaciones = formulaciones.filter(f => f.id !== id);
        setFormulaciones(updatedFormulaciones);
        return {
          success: true,
          message: 'Formulación eliminada exitosamente'
        };
      } else {
        setError(result.message || 'Error al eliminar formulación');
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMsg = 'Error de conexión al eliminar formulación';
      setError(errorMsg);
      console.error('Error deleting formulacion:', err);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [formulaciones]);

  /**
   * Calcular costo total de la formulación
   */
  const calculateTotalCost = useCallback((ingredients) => {
    if (!ingredients || ingredients.length === 0) return 0;
    return ingredients.reduce((total, ing) => {
      const cost = parseFloat(ing.cost_per_unit) || 0;
      const quantity = parseFloat(ing.quantity) || 0;
      return total + (cost * quantity);
    }, 0);
  }, []);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Datos
    formulaciones,

    // Estados
    isLoading,
    error,

    // Funciones
    loadFormulaciones,
    getFormulacionById,
    createFormulacion,
    updateFormulacion,
    deleteFormulacion,
    calculateTotalCost,
    clearError
  };
};
